/**
 * WhiteboardCanvas — a lightweight design/flowchart canvas for "Design"
 * question type answers. Plain HTML5 canvas, no external diagramming
 * library.
 *
 * The board is larger than the visible frame and grows with the drawing:
 * the frame scrolls both ways (scrollbars, hand tool, space+drag, middle
 * mouse) and zooms (buttons, Ctrl/⌘+wheel, +/- keys). Tools: select/move,
 * pen, box, circle, decision, database, line, arrow, wrapping multi-line
 * text (click inside a shape to label it centred), eraser; colours, stroke
 * widths, text sizes, undo/redo, delete, clear, fit-to-content and an
 * expanded (full-window) mode.
 *
 * `getSnapshot()` returns a PNG data URL of the WHOLE drawing (cropped to
 * its content, not just what is scrolled into view, and including a label
 * still being typed), captured on submit and sent to the AI for evaluation
 * alongside the question.
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'
import {
  ArrowUturnLeftIcon, ArrowUturnRightIcon, TrashIcon, HandRaisedIcon, PencilIcon,
  MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, ViewfinderCircleIcon,
  ArrowsPointingOutIcon, ArrowsPointingInIcon, XMarkIcon,
} from '@heroicons/react/24/outline'

export interface WhiteboardCanvasHandle {
  getSnapshot: () => string
  isEmpty: () => boolean
  // Puts a previously saved snapshot (PNG data URL) back on the canvas as one
  // image, so returning to a design question shows the earlier drawing and
  // anything added is saved together with it. Same approach as VMP's
  // whiteboard-canvas loadSnapshot.
  loadSnapshot: (dataUrl: string) => void
}

type Tool = 'select' | 'hand' | 'pen' | 'rect' | 'ellipse' | 'diamond' | 'cylinder' | 'line' | 'arrow' | 'text' | 'eraser'
type ShapeType = 'rect' | 'ellipse' | 'diamond' | 'cylinder' | 'line' | 'arrow'

type Point = { x: number; y: number }

type PathElement = { type: 'path'; points: Point[]; color: string; stroke: number }
type ShapeElement = { type: ShapeType; x1: number; y1: number; x2: number; y2: number; color: string; stroke: number }
type TextElement = { type: 'text'; x: number; y: number; width: number; text: string; color: string; fontSize: number; align: 'left' | 'center' }
// A restored earlier snapshot (see loadSnapshot) — moved/erased as one piece.
type ImageElement = { type: 'image'; x: number; y: number; w: number; h: number; img: HTMLImageElement; color: string }

type CanvasElement = PathElement | ShapeElement | TextElement | ImageElement

type Bounds = { minX: number; minY: number; maxX: number; maxY: number }

type Editor = {
  x: number
  y: number
  width: number
  fontSize: number
  color: string
  align: 'left' | 'center'
  index: number | null
  // Set when the label was placed inside a shape — on commit the label is
  // re-centred vertically on this line however many lines it wrapped to.
  centerY: number | null
}

type Interaction =
  | { kind: 'draw'; draft: PathElement | ShapeElement }
  | { kind: 'move'; index: number; start: Point; orig: CanvasElement; before: CanvasElement[]; moved: boolean; minDx: number; minDy: number; editOnClick: boolean }
  | { kind: 'erase'; before: CanvasElement[]; changed: boolean }
  | { kind: 'pan'; sx: number; sy: number; left: number; top: number }

const COLORS = ['#1f2937', '#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed']
const STROKES = [2, 4]
const FONT_SIZES = [14, 18, 24]
const FONT_FAMILY = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
// Minimum board size (world px); it grows past this as the drawing does.
const BASE_WORLD_W = 1600
const BASE_WORLD_H = 1100
// Always keep this much empty board beyond the furthest element so there is room to keep drawing.
const WORLD_MARGIN = 800
const FRAME_HEIGHT = 480
const GRID = 20
const MIN_ZOOM = 0.25
const MAX_ZOOM = 3
const ZOOM_STEP = 1.2
const MAX_HISTORY = 100
const DEFAULT_TEXT_WIDTH = 240
const MAX_SNAPSHOT_PX = 2000
const SNAPSHOT_PADDING = 40
// Widest (world px) a restored snapshot is placed at — same cap as VMP.
const MAX_RESTORED_IMAGE_W = 1000
// How close (world px) the pointer must pass to an element for the eraser / select tool to hit it.
const HIT_RADIUS = 10

const TOOLS: { id: Tool; label: string; key: string; icon: React.ReactNode }[] = [
  { id: 'select', label: 'Select & move', key: 'V', icon: <SvgIcon><path d="M5 3l13 7.5-6 1.5-2.5 6z" /></SvgIcon> },
  { id: 'hand', label: 'Pan (or hold Space)', key: 'H', icon: <HandRaisedIcon className="w-[18px] h-[18px]" /> },
  { id: 'pen', label: 'Pen', key: 'P', icon: <PencilIcon className="w-[18px] h-[18px]" /> },
  { id: 'rect', label: 'Box', key: 'R', icon: <SvgIcon><rect x="4" y="6" width="16" height="12" rx="1.5" /></SvgIcon> },
  { id: 'ellipse', label: 'Circle', key: 'O', icon: <SvgIcon><ellipse cx="12" cy="12" rx="8" ry="6.5" /></SvgIcon> },
  { id: 'diamond', label: 'Decision', key: 'D', icon: <SvgIcon><path d="M12 3.5l8.5 8.5-8.5 8.5L3.5 12z" /></SvgIcon> },
  { id: 'cylinder', label: 'Database', key: 'C', icon: <SvgIcon><ellipse cx="12" cy="6" rx="7" ry="2.5" /><path d="M5 6v12c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V6" /></SvgIcon> },
  { id: 'line', label: 'Line', key: 'L', icon: <SvgIcon><path d="M5 19L19 5" /></SvgIcon> },
  { id: 'arrow', label: 'Arrow', key: 'A', icon: <SvgIcon><path d="M5 19L19 5M10 5h9v9" /></SvgIcon> },
  { id: 'text', label: 'Text', key: 'T', icon: <SvgIcon><path d="M5 7V5h14v2M12 5v14M9 19h6" /></SvgIcon> },
  { id: 'eraser', label: 'Eraser', key: 'E', icon: <SvgIcon><path d="M9 20h11M4.7 13.3l8.6-8.6a1.5 1.5 0 012.1 0l3.9 3.9a1.5 1.5 0 010 2.1L12 18.1a2 2 0 01-1.4.6H8.4a2 2 0 01-1.4-.6l-2.3-2.3a1.5 1.5 0 010-2.5zM9 9l6 6" /></SvgIcon> },
]

function SvgIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Geometry / text helpers (pure, world coordinates)
// ---------------------------------------------------------------------------

let measureCtx: CanvasRenderingContext2D | null = null
function getMeasureCtx(): CanvasRenderingContext2D {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d')
  return measureCtx as CanvasRenderingContext2D
}

const fontFor = (size: number) => `500 ${size}px ${FONT_FAMILY}`
const lineHeightFor = (size: number) => Math.round(size * 1.35)

/** Word-wraps `text` to `width` (explicit newlines kept, over-long words broken). */
function wrapText(text: string, width: number, fontSize: number): string[] {
  const ctx = getMeasureCtx()
  ctx.font = fontFor(fontSize)
  const lines: string[] = []
  for (const para of text.split('\n')) {
    const words = para.split(/\s+/).filter(Boolean)
    if (words.length === 0) { lines.push(''); continue }
    let line = ''
    for (let word of words) {
      while (word.length > 1 && ctx.measureText(word).width > width) {
        if (line) { lines.push(line); line = '' }
        let cut = word.length - 1
        while (cut > 1 && ctx.measureText(word.slice(0, cut)).width > width) cut--
        lines.push(word.slice(0, cut))
        word = word.slice(cut)
      }
      const candidate = line ? `${line} ${word}` : word
      if (line && ctx.measureText(candidate).width > width) {
        lines.push(line)
        line = word
      } else {
        line = candidate
      }
    }
    lines.push(line)
  }
  return lines
}

function textHeight(el: TextElement): number {
  return wrapText(el.text, el.width, el.fontSize).length * lineHeightFor(el.fontSize)
}

function normRect(el: ShapeElement) {
  return { left: Math.min(el.x1, el.x2), top: Math.min(el.y1, el.y2), right: Math.max(el.x1, el.x2), bottom: Math.max(el.y1, el.y2) }
}

function elementBounds(el: CanvasElement): Bounds {
  if (el.type === 'path') {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const p of el.points) {
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y)
    }
    const s = el.stroke
    return { minX: minX - s, minY: minY - s, maxX: maxX + s, maxY: maxY + s }
  }
  if (el.type === 'text') return { minX: el.x, minY: el.y, maxX: el.x + el.width, maxY: el.y + textHeight(el) }
  if (el.type === 'image') return { minX: el.x, minY: el.y, maxX: el.x + el.w, maxY: el.y + el.h }
  const r = normRect(el)
  const pad = el.type === 'arrow' ? 12 : el.stroke
  return { minX: r.left - pad, minY: r.top - pad, maxX: r.right + pad, maxY: r.bottom + pad }
}

function contentBounds(elements: CanvasElement[]): Bounds | null {
  if (elements.length === 0) return null
  const b = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
  for (const el of elements) {
    const e = elementBounds(el)
    b.minX = Math.min(b.minX, e.minX); b.minY = Math.min(b.minY, e.minY)
    b.maxX = Math.max(b.maxX, e.maxX); b.maxY = Math.max(b.maxY, e.maxY)
  }
  return b
}

function moveElement(el: CanvasElement, dx: number, dy: number): CanvasElement {
  if (el.type === 'path') return { ...el, points: el.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) }
  if (el.type === 'text' || el.type === 'image') return { ...el, x: el.x + dx, y: el.y + dy }
  return { ...el, x1: el.x1 + dx, y1: el.y1 + dy, x2: el.x2 + dx, y2: el.y2 + dy }
}

function distToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lengthSq = dx * dx + dy * dy
  if (lengthSq === 0) return Math.hypot(p.x - a.x, p.y - a.y)
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

function minEdgeDist(pos: Point, pts: Point[]): number {
  let min = Infinity
  for (let i = 0; i < pts.length; i++) min = Math.min(min, distToSegment(pos, pts[i], pts[(i + 1) % pts.length]))
  return min
}

function insideBounds(pos: Point, b: Bounds): boolean {
  return pos.x >= b.minX && pos.x <= b.maxX && pos.y >= b.minY && pos.y <= b.maxY
}

/** Distance from `pos` to the nearest visible part of `el`. */
function distToElement(pos: Point, el: CanvasElement): number {
  if (el.type === 'path') {
    if (el.points.length === 0) return Infinity
    if (el.points.length === 1) return Math.hypot(pos.x - el.points[0].x, pos.y - el.points[0].y)
    let min = Infinity
    for (let i = 0; i < el.points.length - 1; i++) min = Math.min(min, distToSegment(pos, el.points[i], el.points[i + 1]))
    return min
  }
  if (el.type === 'text' || el.type === 'image') return insideBounds(pos, elementBounds(el)) ? 0 : Infinity

  const { x1, y1, x2, y2 } = el
  if (el.type === 'arrow' || el.type === 'line') return distToSegment(pos, { x: x1, y: y1 }, { x: x2, y: y2 })
  if (el.type === 'rect' || el.type === 'cylinder') return minEdgeDist(pos, [{ x: x1, y: y1 }, { x: x2, y: y1 }, { x: x2, y: y2 }, { x: x1, y: y2 }])
  const cx = (x1 + x2) / 2
  const cy = (y1 + y2) / 2
  if (el.type === 'diamond') return minEdgeDist(pos, [{ x: cx, y: y1 }, { x: x2, y: cy }, { x: cx, y: y2 }, { x: x1, y: cy }])
  const rx = Math.abs(x2 - x1) / 2
  const ry = Math.abs(y2 - y1) / 2
  let min = Infinity
  for (let i = 0; i < 48; i++) {
    const angle = (i / 48) * Math.PI * 2
    min = Math.min(min, Math.hypot(pos.x - (cx + rx * Math.cos(angle)), pos.y - (cy + ry * Math.sin(angle))))
  }
  return min
}

const isClosedShape = (el: CanvasElement): el is ShapeElement =>
  el.type === 'rect' || el.type === 'ellipse' || el.type === 'diamond' || el.type === 'cylinder'

/** Top-most element under `pos`: outlines/strokes first, then the inside of text, images and closed shapes. */
function hitTest(pos: Point, elements: CanvasElement[], tolerance: number): number | null {
  for (let i = elements.length - 1; i >= 0; i--) {
    if (distToElement(pos, elements[i]) <= tolerance) return i
  }
  for (let i = elements.length - 1; i >= 0; i--) {
    const el = elements[i]
    if ((el.type === 'text' || el.type === 'image' || isClosedShape(el)) && insideBounds(pos, elementBounds(el))) return i
  }
  return null
}

function drawElement(ctx: CanvasRenderingContext2D, el: CanvasElement) {
  if (el.type === 'image') {
    if (el.img.complete && el.img.naturalWidth > 0) ctx.drawImage(el.img, el.x, el.y, el.w, el.h)
    return
  }
  ctx.strokeStyle = el.color
  ctx.fillStyle = el.color
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'

  if (el.type === 'text') {
    const lh = lineHeightFor(el.fontSize)
    ctx.font = fontFor(el.fontSize)
    ctx.textBaseline = 'top'
    ctx.textAlign = el.align === 'center' ? 'center' : 'left'
    const x = el.align === 'center' ? el.x + el.width / 2 : el.x
    // Small top inset so glyphs sit in the middle of each line box, matching the editor.
    const inset = (lh - el.fontSize) / 2
    wrapText(el.text, el.width, el.fontSize).forEach((line, i) => ctx.fillText(line, x, el.y + inset + i * lh))
    ctx.textAlign = 'left'
    return
  }

  ctx.lineWidth = el.stroke

  if (el.type === 'path') {
    if (el.points.length === 0) return
    ctx.beginPath()
    ctx.moveTo(el.points[0].x, el.points[0].y)
    if (el.points.length === 1) ctx.lineTo(el.points[0].x + 0.01, el.points[0].y)
    for (const p of el.points.slice(1)) ctx.lineTo(p.x, p.y)
    ctx.stroke()
    return
  }

  const { x1, y1, x2, y2 } = el
  const w = x2 - x1
  const h = y2 - y1

  if (el.type === 'rect') {
    const r = normRect(el)
    const radius = Math.min(8, (r.right - r.left) / 4, (r.bottom - r.top) / 4)
    ctx.beginPath()
    ctx.roundRect(r.left, r.top, r.right - r.left, r.bottom - r.top, Math.max(0, radius))
    ctx.stroke()
  } else if (el.type === 'ellipse') {
    ctx.beginPath()
    ctx.ellipse(x1 + w / 2, y1 + h / 2, Math.abs(w / 2), Math.abs(h / 2), 0, 0, Math.PI * 2)
    ctx.stroke()
  } else if (el.type === 'diamond') {
    const cx = x1 + w / 2
    const cy = y1 + h / 2
    ctx.beginPath()
    ctx.moveTo(cx, y1)
    ctx.lineTo(x2, cy)
    ctx.lineTo(cx, y2)
    ctx.lineTo(x1, cy)
    ctx.closePath()
    ctx.stroke()
  } else if (el.type === 'cylinder') {
    const r = normRect(el)
    const cw = r.right - r.left
    const cx = r.left + cw / 2
    const ry = Math.min((r.bottom - r.top) / 2, Math.max(4, cw * 0.12))
    ctx.beginPath()
    ctx.ellipse(cx, r.top + ry, cw / 2, ry, 0, 0, Math.PI * 2)
    ctx.moveTo(r.left, r.top + ry)
    ctx.lineTo(r.left, r.bottom - ry)
    ctx.ellipse(cx, r.bottom - ry, cw / 2, ry, 0, Math.PI, 0, true)
    ctx.lineTo(r.right, r.top + ry)
    ctx.stroke()
  } else {
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()
    if (el.type === 'arrow') {
      const angle = Math.atan2(y2 - y1, x2 - x1)
      const headLen = 8 + el.stroke * 2
      ctx.beginPath()
      ctx.moveTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6))
      ctx.lineTo(x2, y2)
      ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6))
      ctx.stroke()
    }
  }
}

/** PNG of the whole drawing, cropped to its content — independent of scroll/zoom. */
function renderSnapshot(elements: CanvasElement[]): string {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const b = contentBounds(elements)
  if (!ctx || !b) return ''
  const w = b.maxX - b.minX + SNAPSHOT_PADDING * 2
  const h = b.maxY - b.minY + SNAPSHOT_PADDING * 2
  const scale = Math.min(2, MAX_SNAPSHOT_PX / Math.max(w, h))
  canvas.width = Math.max(1, Math.round(w * scale))
  canvas.height = Math.max(1, Math.round(h * scale))
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.setTransform(scale, 0, 0, scale, (SNAPSHOT_PADDING - b.minX) * scale, (SNAPSHOT_PADDING - b.minY) * scale)
  for (const el of elements) drawElement(ctx, el)
  return canvas.toDataURL('image/png')
}

const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z))

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WhiteboardCanvas = forwardRef<WhiteboardCanvasHandle, {}>((_props, ref) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const textInputRef = useRef<HTMLTextAreaElement>(null)

  const [elements, setElements] = useState<CanvasElement[]>([])
  const elementsRef = useRef(elements)
  const pastRef = useRef<CanvasElement[][]>([])
  const futureRef = useRef<CanvasElement[][]>([])
  const [, setHistoryTick] = useState(0)

  const [tool, setTool] = useState<Tool>('pen')
  const [color, setColor] = useState(COLORS[0])
  const [stroke, setStroke] = useState(STROKES[0])
  const [fontSize, setFontSize] = useState(FONT_SIZES[0])
  const [selected, setSelected] = useState<number | null>(null)
  const [zoom, setZoom] = useState(1)
  const [viewport, setViewport] = useState({ w: 0, h: 0 })
  const [expanded, setExpanded] = useState(false)
  const [spaceHeld, setSpaceHeld] = useState(false)
  const [panning, setPanning] = useState(false)

  const [editor, setEditor] = useState<Editor | null>(null)
  const [editorValue, setEditorValue] = useState('')

  const interactionRef = useRef<Interaction | null>(null)
  const pendingScrollRef = useRef<{ left: number; top: number } | null>(null)
  const rafRef = useRef<number | null>(null)

  // Latest values for the imperative draw loop / native listeners.
  const zoomRef = useRef(zoom)
  zoomRef.current = zoom
  const viewportRef = useRef(viewport)
  viewportRef.current = viewport
  const selectedRef = useRef(selected)
  selectedRef.current = selected
  const editorRef = useRef(editor)
  editorRef.current = editor
  const editorValueRef = useRef(editorValue)
  editorValueRef.current = editorValue

  // ---- history ------------------------------------------------------------
  const setLive = (next: CanvasElement[]) => {
    elementsRef.current = next
    setElements(next)
  }
  const pushHistory = (before: CanvasElement[]) => {
    pastRef.current = [...pastRef.current.slice(-(MAX_HISTORY - 1)), before]
    futureRef.current = []
    setHistoryTick((t) => t + 1)
  }
  const commit = (next: CanvasElement[]) => {
    pushHistory(elementsRef.current)
    setLive(next)
  }
  const undo = () => {
    const past = pastRef.current
    if (past.length === 0) return
    futureRef.current = [...futureRef.current, elementsRef.current]
    pastRef.current = past.slice(0, -1)
    setSelected(null)
    setLive(past[past.length - 1])
    setHistoryTick((t) => t + 1)
  }
  const redo = () => {
    const future = futureRef.current
    if (future.length === 0) return
    pastRef.current = [...pastRef.current, elementsRef.current]
    futureRef.current = future.slice(0, -1)
    setSelected(null)
    setLive(future[future.length - 1])
    setHistoryTick((t) => t + 1)
  }
  const deleteSelected = () => {
    if (selected === null) return
    commit(elementsRef.current.filter((_, i) => i !== selected))
    setSelected(null)
  }
  const clear = () => {
    if (elementsRef.current.length === 0) return
    commit([])
    setSelected(null)
  }

  // ---- text editor ----------------------------------------------------------
  /** The label currently being typed, as an element (null when nothing is pending). */
  const pendingTextElement = (): TextElement | null => {
    const ed = editorRef.current
    const value = editorValueRef.current.replace(/\s+$/, '')
    if (!ed || !value.trim()) return null
    const el: TextElement = { type: 'text', x: ed.x, y: ed.y, width: ed.width, text: value, color: ed.color, fontSize: ed.fontSize, align: ed.align }
    if (ed.centerY !== null) el.y = ed.centerY - textHeight(el) / 2
    return el
  }

  const commitText = () => {
    const ed = editorRef.current
    if (!ed) return
    const el = pendingTextElement()
    // Pointerdown and the textarea's blur can both land here for one click.
    editorRef.current = null
    const current = elementsRef.current
    if (ed.index !== null) {
      if (el) commit(current.map((e, i) => (i === ed.index ? el : e)))
      // Cleared back to empty while editing an existing label — remove it.
      else commit(current.filter((_, i) => i !== ed.index))
    } else if (el) {
      commit([...current, el])
    }
    setEditor(null)
    setEditorValue('')
    if (containerRef.current) containerRef.current.focus({ preventScroll: true })
  }

  const openEditorFor = (index: number) => {
    const el = elementsRef.current[index]
    if (!el || el.type !== 'text') return
    setSelected(null)
    setEditor({ x: el.x, y: el.y, width: el.width, fontSize: el.fontSize, color: el.color, align: el.align, index, centerY: null })
    setEditorValue(el.text)
  }

  const openEditorAt = (pos: Point) => {
    // Clicking inside a shape labels the shape: centred, wrapped to its width.
    const current = elementsRef.current
    for (let i = current.length - 1; i >= 0; i--) {
      const el = current[i]
      if (isClosedShape(el)) {
        const r = normRect(el)
        if (pos.x >= r.left && pos.x <= r.right && pos.y >= r.top && pos.y <= r.bottom && r.right - r.left > 30) {
          const inset = el.type === 'diamond' ? (r.right - r.left) / 5 : 8
          const cy = (r.top + r.bottom) / 2
          setEditor({ x: r.left + inset, y: cy - lineHeightFor(fontSize) / 2, width: Math.max(40, r.right - r.left - inset * 2), fontSize, color, align: 'center', index: null, centerY: cy })
          setEditorValue('')
          return
        }
      }
    }
    setEditor({ x: pos.x, y: pos.y - lineHeightFor(fontSize) / 2, width: DEFAULT_TEXT_WIDTH, fontSize, color, align: 'left', index: null, centerY: null })
    setEditorValue('')
  }

  // `autoFocus` alone loses the race with the pointerdown that opened the
  // editor, so focus explicitly once the textarea has committed to the DOM.
  useEffect(() => {
    if (editor && textInputRef.current) {
      const input = textInputRef.current
      input.focus({ preventScroll: true })
      input.setSelectionRange(input.value.length, input.value.length)
    }
  }, [editor === null ? null : editor.index, editor === null])

  // The restored snapshot and the element list right after restoring it: if
  // nothing has changed since, getSnapshot hands back the original PNG instead
  // of re-rendering it (which would add another padding border and lose a
  // little sharpness on every back-and-forth between questions).
  const restoredRef = useRef<{ dataUrl: string; elements: CanvasElement[] } | null>(null)

  useImperativeHandle(ref, () => ({
    getSnapshot: () => {
      const pending = pendingTextElement()
      const ed = editorRef.current
      const restored = restoredRef.current
      if (restored && restored.elements === elementsRef.current && !pending) return restored.dataUrl
      let all = elementsRef.current
      if (ed && ed.index !== null) all = all.filter((_, i) => i !== ed.index)
      return renderSnapshot(pending ? [...all, pending] : all)
    },
    isEmpty: () => elementsRef.current.length === 0 && pendingTextElement() === null,
    loadSnapshot: (dataUrl: string) => {
      // Once per canvas instance (the canvas is re-created per question).
      if (!dataUrl || restoredRef.current) return
      restoredRef.current = { dataUrl, elements: [] }
      const img = new Image()
      img.onload = () => {
        const scale = Math.min(1, MAX_RESTORED_IMAGE_W / Math.max(1, img.naturalWidth))
        const el: ImageElement = { type: 'image', x: 0, y: 0, w: img.naturalWidth * scale, h: img.naturalHeight * scale, img, color: '#000000' }
        const next = [el, ...elementsRef.current]
        restoredRef.current = { dataUrl, elements: next }
        setLive(next)
      }
      img.src = dataUrl
    },
  }))

  // ---- rendering ------------------------------------------------------------
  const draw = useCallback(() => {
    rafRef.current = null
    const canvas = canvasRef.current
    const scroller = scrollRef.current
    if (!canvas || !scroller) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const { w, h } = viewportRef.current
    if (w === 0 || h === 0) return
    const dpr = window.devicePixelRatio || 1
    const pw = Math.round(w * dpr)
    const ph = Math.round(h * dpr)
    if (canvas.width !== pw || canvas.height !== ph) {
      canvas.width = pw
      canvas.height = ph
    }
    const z = zoomRef.current
    const sl = scroller.scrollLeft
    const st = scroller.scrollTop

    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, pw, ph)
    ctx.setTransform(dpr * z, 0, 0, dpr * z, -sl * dpr, -st * dpr)

    // Dot grid (screen only — never part of the snapshot).
    if (GRID * z >= 8) {
      const x0 = Math.floor(sl / z / GRID) * GRID
      const y0 = Math.floor(st / z / GRID) * GRID
      const x1 = (sl + w) / z
      const y1 = (st + h) / z
      const dot = 1.6 / z
      ctx.fillStyle = '#d6dae1'
      for (let x = x0; x <= x1; x += GRID) {
        for (let y = y0; y <= y1; y += GRID) ctx.fillRect(x - dot / 2, y - dot / 2, dot, dot)
      }
    }

    const ed = editorRef.current
    elementsRef.current.forEach((el, i) => {
      if (ed && ed.index === i) return
      drawElement(ctx, el)
    })
    const interaction = interactionRef.current
    if (interaction && interaction.kind === 'draw') drawElement(ctx, interaction.draft)

    const sel = selectedRef.current
    if (sel !== null && elementsRef.current[sel]) {
      const b = elementBounds(elementsRef.current[sel])
      const pad = 6
      ctx.save()
      ctx.setLineDash([5 / z, 4 / z])
      ctx.lineWidth = 1.25 / z
      ctx.strokeStyle = '#3b82f6'
      ctx.strokeRect(b.minX - pad, b.minY - pad, b.maxX - b.minX + pad * 2, b.maxY - b.minY + pad * 2)
      ctx.restore()
    }
  }, [])

  const scheduleDraw = useCallback(() => {
    if (rafRef.current === null) rafRef.current = requestAnimationFrame(draw)
  }, [draw])

  useEffect(() => { scheduleDraw() }, [elements, zoom, viewport, selected, editor, scheduleDraw])
  useEffect(() => () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current) }, [])

  // Track the visible frame size (excludes scrollbars).
  useEffect(() => {
    const scroller = scrollRef.current
    if (!scroller) return
    const update = () => setViewport({ w: scroller.clientWidth, h: scroller.clientHeight })
    update()
    const ro = new ResizeObserver(update)
    ro.observe(scroller)
    return () => ro.disconnect()
  }, [])

  // Apply scroll position requested by a zoom change once the board has been resized for it.
  useLayoutEffect(() => {
    const scroller = scrollRef.current
    const pending = pendingScrollRef.current
    if (!scroller || !pending) return
    pendingScrollRef.current = null
    scroller.scrollLeft = Math.max(0, pending.left)
    scroller.scrollTop = Math.max(0, pending.top)
    scheduleDraw()
  }, [zoom, scheduleDraw])

  // ---- zoom -----------------------------------------------------------------
  /** Zoom keeping the world point under screen offset (sx, sy) of the frame fixed. */
  const zoomTo = useCallback((nextZoom: number, sx?: number, sy?: number) => {
    const scroller = scrollRef.current
    if (!scroller) return
    const z = zoomRef.current
    const nz = clampZoom(nextZoom)
    if (nz === z) return
    const ax = sx === undefined ? scroller.clientWidth / 2 : sx
    const ay = sy === undefined ? scroller.clientHeight / 2 : sy
    const wx = (scroller.scrollLeft + ax) / z
    const wy = (scroller.scrollTop + ay) / z
    pendingScrollRef.current = { left: wx * nz - ax, top: wy * nz - ay }
    setZoom(nz)
  }, [])

  const fitToContent = () => {
    const scroller = scrollRef.current
    const b = contentBounds(elementsRef.current)
    if (!scroller) return
    if (!b) {
      pendingScrollRef.current = { left: 0, top: 0 }
      if (zoomRef.current === 1) { scroller.scrollLeft = 0; scroller.scrollTop = 0 } else setZoom(1)
      return
    }
    const pad = 40
    const w = b.maxX - b.minX + pad * 2
    const h = b.maxY - b.minY + pad * 2
    const nz = clampZoom(Math.min(scroller.clientWidth / w, scroller.clientHeight / h, 1.5))
    const left = (b.minX - pad) * nz - Math.max(0, (scroller.clientWidth - w * nz) / 2)
    const top = (b.minY - pad) * nz - Math.max(0, (scroller.clientHeight - h * nz) / 2)
    if (nz === zoomRef.current) {
      scroller.scrollLeft = Math.max(0, left)
      scroller.scrollTop = Math.max(0, top)
    } else {
      pendingScrollRef.current = { left, top }
      setZoom(nz)
    }
  }

  // Ctrl/⌘ + wheel (and trackpad pinch) zooms around the cursor; plain wheel scrolls natively.
  useEffect(() => {
    const scroller = scrollRef.current
    if (!scroller) return
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      const rect = scroller.getBoundingClientRect()
      const factor = Math.exp(-e.deltaY * 0.0025)
      zoomTo(zoomRef.current * factor, e.clientX - rect.left, e.clientY - rect.top)
    }
    scroller.addEventListener('wheel', onWheel, { passive: false })
    return () => scroller.removeEventListener('wheel', onWheel)
  }, [zoomTo])

  // ---- pointer input --------------------------------------------------------
  const toWorld = (e: { clientX: number; clientY: number }): Point => {
    const canvas = canvasRef.current
    const scroller = scrollRef.current
    if (!canvas || !scroller) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    const z = zoomRef.current
    return {
      x: Math.max(0, (e.clientX - rect.left + scroller.scrollLeft) / z),
      y: Math.max(0, (e.clientY - rect.top + scroller.scrollTop) / z),
    }
  }

  const startMove = (index: number, pos: Point, editOnClick: boolean) => {
    const el = elementsRef.current[index]
    const b = elementBounds(el)
    interactionRef.current = {
      kind: 'move', index, start: pos, orig: el, before: elementsRef.current, moved: false,
      minDx: -b.minX, minDy: -b.minY, editOnClick,
    }
  }

  const eraseAt = (pos: Point) => {
    const interaction = interactionRef.current
    if (!interaction || interaction.kind !== 'erase') return
    const current = elementsRef.current
    const idx = hitTest(pos, current, HIT_RADIUS / Math.min(1, zoomRef.current))
    if (idx === null) return
    interaction.changed = true
    setLive(current.filter((_, i) => i !== idx))
  }

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (editorRef.current) {
      // A click outside the label being typed just finishes it.
      e.preventDefault()
      commitText()
      return
    }
    e.preventDefault()
    if (containerRef.current) containerRef.current.focus({ preventScroll: true })
    if (e.button === 2) return
    e.currentTarget.setPointerCapture(e.pointerId)
    const scroller = scrollRef.current
    if (e.button === 1 || tool === 'hand' || spaceHeld) {
      if (!scroller) return
      interactionRef.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, left: scroller.scrollLeft, top: scroller.scrollTop }
      setPanning(true)
      return
    }
    const pos = toWorld(e)
    const tolerance = HIT_RADIUS / Math.min(1, zoomRef.current)

    if (tool === 'select') {
      const idx = hitTest(pos, elementsRef.current, tolerance)
      setSelected(idx)
      if (idx !== null) startMove(idx, pos, false)
      return
    }
    if (tool === 'text') {
      const current = elementsRef.current
      for (let i = current.length - 1; i >= 0; i--) {
        const el = current[i]
        if (el.type === 'text' && insideBounds(pos, elementBounds(el))) {
          startMove(i, pos, true)
          return
        }
      }
      openEditorAt(pos)
      return
    }
    if (tool === 'eraser') {
      interactionRef.current = { kind: 'erase', before: elementsRef.current, changed: false }
      eraseAt(pos)
      return
    }
    setSelected(null)
    const draft: PathElement | ShapeElement = tool === 'pen'
      ? { type: 'path', points: [pos], color, stroke }
      : { type: tool, x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y, color, stroke }
    interactionRef.current = { kind: 'draw', draft }
    scheduleDraw()
  }

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const interaction = interactionRef.current
    if (!interaction) return
    if (interaction.kind === 'pan') {
      const scroller = scrollRef.current
      if (!scroller) return
      scroller.scrollLeft = interaction.left - (e.clientX - interaction.sx)
      scroller.scrollTop = interaction.top - (e.clientY - interaction.sy)
      return
    }
    const pos = toWorld(e)
    if (interaction.kind === 'erase') { eraseAt(pos); return }
    if (interaction.kind === 'move') {
      const dx = Math.max(interaction.minDx, pos.x - interaction.start.x)
      const dy = Math.max(interaction.minDy, pos.y - interaction.start.y)
      if (!interaction.moved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return
      interaction.moved = true
      const moved = moveElement(interaction.orig, dx, dy)
      setLive(elementsRef.current.map((el, i) => (i === interaction.index ? moved : el)))
      return
    }
    const d = interaction.draft
    if (d.type === 'path') {
      d.points.push(pos)
    } else {
      let x2 = pos.x
      let y2 = pos.y
      // Shift: square/circle for shapes, 45° steps for lines and arrows.
      if (e.shiftKey) {
        const dx = x2 - d.x1
        const dy = y2 - d.y1
        if (d.type === 'line' || d.type === 'arrow') {
          const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4)
          const len = Math.hypot(dx, dy)
          x2 = d.x1 + len * Math.cos(angle)
          y2 = d.y1 + len * Math.sin(angle)
        } else {
          const size = Math.max(Math.abs(dx), Math.abs(dy))
          x2 = d.x1 + Math.sign(dx || 1) * size
          y2 = d.y1 + Math.sign(dy || 1) * size
        }
      }
      d.x2 = Math.max(0, x2)
      d.y2 = Math.max(0, y2)
    }
    scheduleDraw()
  }

  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const interaction = interactionRef.current
    interactionRef.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    if (!interaction) return
    if (interaction.kind === 'pan') { setPanning(false); return }
    if (interaction.kind === 'erase') {
      if (interaction.changed) pushHistory(interaction.before)
      return
    }
    if (interaction.kind === 'move') {
      if (interaction.moved) pushHistory(interaction.before)
      else if (interaction.editOnClick) openEditorFor(interaction.index)
      return
    }
    const d = interaction.draft
    if (d.type !== 'path' && Math.abs(d.x2 - d.x1) < 4 && Math.abs(d.y2 - d.y1) < 4) {
      // A plain click with a shape tool — don't leave an invisible speck behind.
      scheduleDraw()
      return
    }
    commit([...elementsRef.current, d])
  }

  const onDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (tool !== 'select' || editorRef.current) return
    const pos = toWorld(e)
    const idx = hitTest(pos, elementsRef.current, HIT_RADIUS)
    if (idx !== null && elementsRef.current[idx].type === 'text') openEditorFor(idx)
    else openEditorAt(pos)
  }

  // ---- styling changes also restyle the selected element ----------------------
  const restyleSelected = (patch: { color?: string; stroke?: number; fontSize?: number }) => {
    if (selected === null) return
    const el = elementsRef.current[selected]
    if (!el || el.type === 'image') return
    let next: CanvasElement = el
    if (patch.color !== undefined) next = { ...next, color: patch.color }
    if (patch.stroke !== undefined && next.type !== 'text') next = { ...next, stroke: patch.stroke }
    if (patch.fontSize !== undefined && next.type === 'text') next = { ...next, fontSize: patch.fontSize }
    if (next !== el) commit(elementsRef.current.map((x, i) => (i === selected ? next : x)))
  }
  const pickColor = (c: string) => { setColor(c); restyleSelected({ color: c }) }
  const pickStroke = (s: number) => { setStroke(s); restyleSelected({ stroke: s }) }
  const pickFontSize = (s: number) => { setFontSize(s); restyleSelected({ fontSize: s }) }

  const chooseTool = (t: Tool) => {
    if (editorRef.current) commitText()
    setTool(t)
    if (t !== 'select') setSelected(null)
  }

  // ---- keyboard ---------------------------------------------------------------
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== containerRef.current) return
    const mod = e.ctrlKey || e.metaKey
    const key = e.key.toLowerCase()
    if (mod && key === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return }
    if (mod && key === 'y') { e.preventDefault(); redo(); return }
    if (mod) return
    if (e.key === ' ') { e.preventDefault(); setSpaceHeld(true); return }
    if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); deleteSelected(); return }
    if (e.key === 'Escape') {
      if (selected !== null) setSelected(null)
      else if (expanded) setExpanded(false)
      return
    }
    if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomTo(zoomRef.current * ZOOM_STEP); return }
    if (e.key === '-') { e.preventDefault(); zoomTo(zoomRef.current / ZOOM_STEP); return }
    if (e.key === '0') { e.preventDefault(); zoomTo(1); return }
    const match = TOOLS.find((t) => t.key.toLowerCase() === key)
    if (match) { e.preventDefault(); chooseTool(match.id) }
  }
  const onKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ') setSpaceHeld(false)
  }

  // ---- layout -----------------------------------------------------------------
  const bounds = contentBounds(elements)
  const worldW = Math.max(BASE_WORLD_W, bounds ? bounds.maxX + WORLD_MARGIN : 0)
  const worldH = Math.max(BASE_WORLD_H, bounds ? bounds.maxY + WORLD_MARGIN : 0)
  const spacerW = Math.max(worldW * zoom, viewport.w)
  const spacerH = Math.max(worldH * zoom, viewport.h)

  const selectedEl = selected !== null ? elements[selected] : undefined
  const showFontSizes = tool === 'text' || editor !== null || (selectedEl !== undefined && selectedEl.type === 'text')

  let cursor = 'crosshair'
  if (panning) cursor = 'grabbing'
  else if (tool === 'hand' || spaceHeld) cursor = 'grab'
  else if (tool === 'select') cursor = 'default'
  else if (tool === 'text') cursor = 'text'
  else if (tool === 'eraser') cursor = 'cell'

  const editorLineHeight = editor ? lineHeightFor(editor.fontSize) : 0
  const editorLines = editor ? Math.max(1, wrapText(editorValue, editor.width, editor.fontSize).length) : 1

  const iconBtn = (active: boolean) =>
    `inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${
      active ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200/70 hover:text-gray-900'
    }`
  const plainBtn = 'inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-600 hover:bg-gray-200/70 hover:text-gray-900 disabled:opacity-35 disabled:hover:bg-transparent'
  const divider = <span className="w-px h-6 bg-gray-200 mx-1" aria-hidden="true" />

  const board = (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onBlur={() => setSpaceHeld(false)}
      className={`flex flex-col bg-white border border-gray-300 overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-blue-200 ${
        expanded ? 'fixed inset-3 sm:inset-6 z-[61] rounded-xl shadow-2xl' : 'rounded-xl'
      }`}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-x-3 gap-y-1.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex-wrap">
        <div className="flex items-center gap-0.5 flex-wrap" role="toolbar" aria-label="Drawing tools">
          {TOOLS.map((t, i) => (
            <React.Fragment key={t.id}>
              {(i === 2 || i === 10) && divider}
              <button
                type="button"
                onClick={() => chooseTool(t.id)}
                className={iconBtn(tool === t.id)}
                title={`${t.label} (${t.key})`}
                aria-label={t.label}
                aria-pressed={tool === t.id}
              >
                {t.icon}
              </button>
            </React.Fragment>
          ))}
        </div>

        <div className="flex items-center gap-0.5 flex-wrap">
          <div className="flex items-center gap-1 px-1">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => pickColor(c)}
                className={`w-5 h-5 rounded-full ring-offset-1 ring-offset-gray-50 ${color === c ? 'ring-2 ring-gray-500' : 'hover:ring-2 hover:ring-gray-300'}`}
                style={{ backgroundColor: c }}
                title="Colour"
                aria-label={`Colour ${c}`}
              />
            ))}
          </div>
          {divider}
          {showFontSizes ? (
            FONT_SIZES.map((s, i) => (
              <button key={s} type="button" onClick={() => pickFontSize(s)} className={iconBtn(fontSize === s)} title={`Text size ${['small', 'medium', 'large'][i]}`} aria-label={`Text size ${['small', 'medium', 'large'][i]}`}>
                <span className="font-semibold leading-none" style={{ fontSize: 11 + i * 3 }}>A</span>
              </button>
            ))
          ) : (
            STROKES.map((s, i) => (
              <button key={s} type="button" onClick={() => pickStroke(s)} className={iconBtn(stroke === s)} title={i === 0 ? 'Thin stroke' : 'Thick stroke'} aria-label={i === 0 ? 'Thin stroke' : 'Thick stroke'}>
                <span className="block w-4 rounded-full bg-current" style={{ height: s }} />
              </button>
            ))
          )}
          {divider}
          <button type="button" onClick={undo} disabled={pastRef.current.length === 0} className={plainBtn} title="Undo (Ctrl+Z)" aria-label="Undo">
            <ArrowUturnLeftIcon className="w-4 h-4" />
          </button>
          <button type="button" onClick={redo} disabled={futureRef.current.length === 0} className={plainBtn} title="Redo (Ctrl+Y)" aria-label="Redo">
            <ArrowUturnRightIcon className="w-4 h-4" />
          </button>
          <button type="button" onClick={deleteSelected} disabled={selected === null} className={plainBtn} title="Delete selected (Del)" aria-label="Delete selected">
            <XMarkIcon className="w-4 h-4" />
          </button>
          <button type="button" onClick={clear} disabled={elements.length === 0} className={`${plainBtn} hover:text-red-600`} title="Clear board" aria-label="Clear board">
            <TrashIcon className="w-4 h-4" />
          </button>
          {divider}
          <button type="button" onClick={() => zoomTo(zoom / ZOOM_STEP)} disabled={zoom <= MIN_ZOOM} className={plainBtn} title="Zoom out (−)" aria-label="Zoom out">
            <MagnifyingGlassMinusIcon className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => zoomTo(1)} className="h-8 min-w-[46px] px-1 rounded-md text-xs font-medium text-gray-600 hover:bg-gray-200/70 tabular-nums" title="Reset zoom to 100% (0)">
            {Math.round(zoom * 100)}%
          </button>
          <button type="button" onClick={() => zoomTo(zoom * ZOOM_STEP)} disabled={zoom >= MAX_ZOOM} className={plainBtn} title="Zoom in (+)" aria-label="Zoom in">
            <MagnifyingGlassPlusIcon className="w-4 h-4" />
          </button>
          <button type="button" onClick={fitToContent} className={plainBtn} title="Fit drawing to view" aria-label="Fit drawing to view">
            <ViewfinderCircleIcon className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => setExpanded((v) => !v)} className={plainBtn} title={expanded ? 'Exit expanded view (Esc)' : 'Expand board'} aria-label={expanded ? 'Exit expanded view' : 'Expand board'}>
            {expanded ? <ArrowsPointingInIcon className="w-4 h-4" /> : <ArrowsPointingOutIcon className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Scrollable, zoomable board */}
      <div
        ref={scrollRef}
        onScroll={scheduleDraw}
        className={`relative overflow-auto bg-white ${expanded ? 'flex-1 min-h-0' : ''}`}
        style={expanded ? undefined : { height: FRAME_HEIGHT }}
      >
        <div className="relative" style={{ width: spacerW, height: spacerH }}>
          <canvas
            ref={canvasRef}
            className="sticky left-0 top-0 block touch-none select-none"
            style={{ width: viewport.w, height: viewport.h, cursor }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onDoubleClick={onDoubleClick}
            onContextMenu={(e) => e.preventDefault()}
          />
          {editor && (
            <textarea
              ref={textInputRef}
              value={editorValue}
              onChange={(e) => setEditorValue(e.target.value)}
              onBlur={commitText}
              onKeyDown={(e) => {
                e.stopPropagation()
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitText() }
                else if (e.key === 'Escape') { e.preventDefault(); commitText() }
              }}
              onKeyUp={(e) => e.stopPropagation()}
              onPointerUp={(e) => {
                // Width dragged with the resize handle — wrap to the new width.
                const w = e.currentTarget.offsetWidth / zoom
                if (Math.abs(w - editor.width) > 2) setEditor({ ...editor, width: Math.max(40, w) })
              }}
              rows={1}
              spellCheck
              placeholder="Type a label…"
              className="absolute z-10 m-0 p-0 bg-white/90 border-0 outline outline-2 outline-offset-2 outline-blue-400 rounded-sm overflow-hidden placeholder:text-gray-400"
              style={{
                left: editor.x * zoom,
                top: editor.y * zoom,
                width: editor.width * zoom,
                height: editorLines * editorLineHeight * zoom,
                fontFamily: FONT_FAMILY,
                fontWeight: 500,
                fontSize: editor.fontSize * zoom,
                lineHeight: `${editorLineHeight * zoom}px`,
                color: editor.color,
                textAlign: editor.align,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
                resize: editor.align === 'left' ? 'horizontal' : 'none',
                minWidth: 40,
              }}
            />
          )}
        </div>
      </div>

      <p className="px-3 py-1.5 text-[11px] leading-relaxed text-gray-500 bg-gray-50 border-t border-gray-200">
        Boxes/circles/databases for components, decisions for branches, arrows for flow. Text wraps automatically —
        <span className="font-medium"> Enter</span> to finish, <span className="font-medium">Shift+Enter</span> for a new line;
        click inside a shape to label it. Scroll or hold <span className="font-medium">Space</span> and drag to pan,
        <span className="font-medium"> Ctrl+wheel</span> to zoom. Shift keeps shapes square and arrows straight.
      </p>
    </div>
  )

  // Same tree shape in both modes so the board never remounts (its refs,
  // observers and listeners are bound once) when expanding/collapsing.
  return (
    <>
      {/* Keeps the page layout from jumping while the board is expanded. */}
      {expanded && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-xs text-gray-400" style={{ height: FRAME_HEIGHT + 90 }}>
          Board is open in expanded view
        </div>
      )}
      {expanded && <div className="fixed inset-0 z-[60] bg-gray-900/40" onClick={() => setExpanded(false)} aria-hidden="true" />}
      {board}
    </>
  )
})

WhiteboardCanvas.displayName = 'WhiteboardCanvas'

export default WhiteboardCanvas
